import { useState } from "react";

import { createFileRoute } from "@tanstack/react-router";

import { Plus } from "lucide-react";

import type { Prospect } from "../api/hooks";
import {
  useCreateProspect,
  useMoveProspectStage,
  useProspects,
} from "../api/hooks";
import { dateLabel } from "../lib/relativeTime";

import { OrganisationForm } from "./-OrganisationForm";

import styles from "./lead-research.module.scss";

// How long a find has been sitting unjudged, which is the only ordering this
// queue has: fit is a scout output and there is no scout yet, so by hand the
// oldest is the one to look at.
function daysWaiting(discoveredAt: string): number {
  const days = Math.floor((Date.now() - new Date(discoveredAt).getTime()) / 86_400_000);

  return Math.max(0, days);
}

function waitingLabel(discoveredAt: string): string {
  const days = daysWaiting(discoveredAt);

  if (days === 0) return "today";
  if (days === 1) return "1 day";

  return `${days} days`;
}

function hectaresLabel(hectares: number | null): string {
  if (hectares === null) return "-";
  if (hectares > 0 && hectares < 1) return "<1 ha";

  return `${Math.round(hectares).toLocaleString("en-GB")} ha`;
}

function FindRow({ find }: { find: Prospect }) {
  const move = useMoveProspectStage();
  const [dismissing, setDismissing] = useState(false);
  const [reason, setReason] = useState("");
  const [message, setMessage] = useState("");

  function decide(stage: "to_contact" | "dismissed") {
    setMessage("");

    if (stage === "dismissed" && reason.trim() === "") {
      // The API refuses it too. Asking here saves the round trip, and the
      // reason is the whole point of keeping a dismissal: it is what stops the
      // scout offering the same body again next quarter.
      setMessage("Say why this one is not a fit.");
      return;
    }

    move.mutate(
      { id: find.id, stage, dismissedReason: reason },
      { onError: () => setMessage("That could not be saved. Try again.") },
    );
  }

  return (
    <tr>
      <td>
        <span className="tbl-org">{find.organisationName}</span>
        {find.organisationKind !== "" && (
          <span className={styles.rowKind}>{find.organisationKind}</span>
        )}
      </td>
      <td>{find.organisationCounty || "-"}</td>
      <td className="tbl-right">{hectaresLabel(find.organisationHectares)}</td>
      <td className={styles.rationale}>
        {find.rationale === "" ? (
          <span className={styles.noRationale}>Nobody has said why yet</span>
        ) : (
          find.rationale
        )}
      </td>
      <td className={styles.waiting}>{waitingLabel(find.discoveredAt)}</td>
      <td>
        {!dismissing && (
          <span className={styles.decision}>
            <button
              type="button"
              className={styles.dismiss}
              disabled={move.isPending}
              onClick={() => setDismissing(true)}
            >
              Not a fit
            </button>
            <button
              type="button"
              className={styles.approve}
              disabled={move.isPending}
              onClick={() => decide("to_contact")}
            >
              Add to pipeline
            </button>
          </span>
        )}

        {dismissing && (
          <span className={styles.decision}>
            {/* Asked for beside the decision, not after it: a dismissal with no
                reason is a row nobody can act on next quarter, which is the
                whole reason dismissed finds are kept rather than deleted. */}
            <input
              type="text"
              className={styles.reason}
              aria-label={`Why ${find.organisationName} is not a fit`}
              placeholder="Why not?"
              value={reason}
              disabled={move.isPending}
              onChange={(event) => setReason(event.target.value)}
            />
            <button
              type="button"
              className={styles.dismiss}
              disabled={move.isPending}
              onClick={() => decide("dismissed")}
            >
              Dismiss
            </button>
            <button
              type="button"
              className={styles.cancel}
              disabled={move.isPending}
              onClick={() => {
                setDismissing(false);
                setMessage("");
              }}
            >
              Cancel
            </button>
          </span>
        )}

        {message !== "" && (
          <span className={styles.error} role="alert">
            {message}
          </span>
        )}
      </td>
    </tr>
  );
}

function LeadResearch() {
  const [adding, setAdding] = useState(false);
  const createProspect = useCreateProspect();

  // Oldest first, because fit is a scout output and there is no scout: by hand
  // the queue is worked in the order things arrived.
  const finds = useProspects({ stages: ["found"], oldestFirst: true });
  const dismissed = useProspects({ stages: ["dismissed"] });

  const findRows = finds.data?.pages.flatMap((page) => page.items) ?? [];
  const findTotal = finds.data?.pages[0]?.count;

  const dismissedRows = dismissed.data?.pages.flatMap((page) => page.items) ?? [];
  const dismissedTotal = dismissed.data?.pages[0]?.count;

  // The head of the queue is the oldest, because that is the order it is in.
  const oldest = findRows[0];

  return (
    <>
      <div className={styles.pageHead}>
        <div className="page-header">
          <div className="page-title">Lead research</div>
          <div className="page-subtitle">
            Organisations to judge before anyone contacts them
          </div>
        </div>

        <button
          type="button"
          className={styles.add}
          onClick={() => setAdding(true)}
        >
          <Plus size={14} />
          Add organisation
        </button>
      </div>

      <div className="panel" aria-busy={finds.isPlaceholderData}>
        <div className={styles.head}>
          <span className={styles.headTitle}>New finds to review</span>
          {findTotal !== undefined && findTotal > 0 && (
            <span className={styles.headCount}>
              {findTotal.toLocaleString("en-GB")} waiting
            </span>
          )}
        </div>

        {finds.isPending && (
          <div className={styles.state} role="status">
            Loading the finds…
          </div>
        )}

        {finds.isError && (
          <div className={styles.state} role="alert">
            The finds could not be loaded.
          </div>
        )}

        {!finds.isPending && !finds.isError && findRows.length === 0 && (
          <div className={styles.state} role="status">
            Nothing waiting. Finds appear here for judging before anyone contacts
            them.
          </div>
        )}

        {findRows.length > 0 && (
          <>
            <table className="tbl" aria-label="New finds to review">
              <thead>
                <tr>
                  <th>Organisation</th>
                  <th>County</th>
                  <th className="tbl-right">Size</th>
                  <th>Why it was flagged</th>
                  <th>Waiting</th>
                  <th>Decision</th>
                </tr>
              </thead>
              <tbody>
                {findRows.map((find) => (
                  <FindRow key={find.id} find={find} />
                ))}
              </tbody>
            </table>

            <div className={styles.foot}>
              <span className={styles.footNote}>
                {findRows.length.toLocaleString("en-GB")} of{" "}
                {(findTotal ?? findRows.length).toLocaleString("en-GB")} shown
                {oldest !== undefined &&
                  ` · oldest has been waiting ${waitingLabel(oldest.discoveredAt)}`}
              </span>

              {/* Named apart from the other list's: two buttons reading "Load
                  more" are two controls a screen reader cannot tell apart, and
                  neither can a test. */}
              {finds.hasNextPage && (
                <button
                  type="button"
                  className={styles.loadMore}
                  aria-label="Load more finds"
                  disabled={finds.isFetchingNextPage}
                  onClick={() => finds.fetchNextPage()}
                >
                  {finds.isFetchingNextPage ? "Loading…" : "Load more"}
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Kept rather than deleted, and shown rather than filed away: the reason
          is what stops the same body being offered again, and it is only worth
          anything if somebody can read it - which means being able to reach it,
          not just the first page of it. */}
      <div className="panel">
        <div className={styles.head}>
          <span className={styles.headTitle}>Dismissed at review</span>
          {dismissedTotal !== undefined && dismissedTotal > 0 && (
            <span className={styles.headCount}>
              {dismissedTotal.toLocaleString("en-GB")} so far
            </span>
          )}
        </div>

        {dismissed.isError && (
          <div className={styles.state} role="alert">
            The dismissed finds could not be loaded.
          </div>
        )}

        {!dismissed.isPending && !dismissed.isError && dismissedRows.length === 0 && (
          <div className={styles.state} role="status">
            Nothing dismissed yet.
          </div>
        )}

        {dismissedRows.length > 0 && (
          <>
            <div className={styles.dismissedList}>
              {dismissedRows.map((find) => (
                <div key={find.id} className={styles.dismissedCard}>
                  <div className={styles.dismissedName}>{find.organisationName}</div>
                  <div className={styles.dismissedReason}>
                    {find.dismissedReason === ""
                      ? "No reason recorded"
                      : find.dismissedReason}
                  </div>
                  <div className={styles.dismissedWhen}>
                    Found {dateLabel(find.discoveredAt)}
                  </div>
                </div>
              ))}
            </div>

            {/* A count with one page under it is a panel that hides the thing
                it exists to show: twenty-five dismissals under a heading
                saying twenty-five, of which five are unreachable. */}
            <div className={styles.foot}>
              <span className={styles.footNote}>
                {dismissedRows.length.toLocaleString("en-GB")} of{" "}
                {(dismissedTotal ?? dismissedRows.length).toLocaleString("en-GB")} shown
              </span>

              {dismissed.hasNextPage && (
                <button
                  type="button"
                  className={styles.loadMore}
                  aria-label="Load more dismissed finds"
                  disabled={dismissed.isFetchingNextPage}
                  onClick={() => dismissed.fetchNextPage()}
                >
                  {dismissed.isFetchingNextPage ? "Loading…" : "Load more"}
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {adding && (
        <OrganisationForm
          record={null}
          title="Add a find"
          onClose={() => setAdding(false)}
          onCreated={(created) =>
            createProspect.mutateAsync({
              organisationId: created.id,
              // Added here, so it joins the queue being judged rather than the
              // one being contacted.
              stage: "found",
            })
          }
          onCreatedFailureMessage={(created) =>
            `${created.name} was added, but putting it in the review queue failed. Try again, or find it in the organisations list.`
          }
        />
      )}
    </>
  );
}

export const Route = createFileRoute("/lead-research")({
  component: LeadResearch,
});
